/** @format */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PaperedTable from '../../../components/Table/PaperedTable';
import { TableBody, TableCell, TableFooter, TableHead, TableRow } from '@material-ui/core';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import Filter from '../../../interfaces/forms/Filter';
import Order from '../../../interfaces/forms/Order';
import { useDispatch, useSelector } from 'react-redux';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import FilterList from '../../../components/Filter/FilterList';
import Pager from '../../../components/Adescom/PagerRange';
import { FilterReducerFieldName, FilterReducers } from '../../../modules/filter/FilterReducers';
import { FilterListReducerFieldName } from '../../../modules/filter/FilterListReducers';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import FabAddButton from '../../../components/Buttons/FabAddButton';
import ARReportAddDialog from './ARReportAddDialog';
import ReportItem from './ReportItem';
import { DocumentCollectionInterface, DocumentInterface } from '../../../interfaces/platform/DocumentInterface';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { documentConstants } from '../../../modules/document/actions';
import { DownloadFile } from '../../../helpers/DownloadHelper';
import { FilterManager } from '../../../modules/filter/FilterManager';
import { PreprocessFilters } from '../../../helpers/FilterHelper';
import oplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';
import { withStyles } from '@material-ui/core/styles';

type ARReportListType = {
    classes: any;
    showMainLoader(show: boolean): void;
};

const _ = require('underscore');

const ARReportList = (props: ARReportListType): React.ReactElement => {
    const setDefaultFilters = (): Array<Filter> => [
        { name: 'orType[]', value: '3' },
        {
            name: 'orType[]',
            value: '4',
        },
        {
            name: 'orType[]',
            value: '6',
        },
        { name: 'orType[]', value: '8' },
    ];
    const setDefaultOrders = (): Array<Order> => [{ name: 'createdAt', value: 'DESC' }];
    const { showMainLoader, classes } = props;
    const dispatch = useDispatch();
    const { headerFilterElementHasValue, headerFilterElementName } = useSelector((state: { filterReducer: FilterReducers }) => state.filterReducer);
    const [showClearFilterButton, setShowClearFilterButton] = useState(false);
    const [reports, setReports] = useState<DocumentCollectionInterface>();
    const [reload, setReload] = useState(false);
    const [showAdd, setShowAdd] = useState<boolean>(false);
    const itemLoader: ItemLoaders = useMemo(() => {
        return new ItemLoaders(dispatch);
    }, [dispatch]);
    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableStickyCommand = { root: props.classes.tableStickyCommand };
    const [reportType, setReportType] = useState([
        { name: 'documents:report.type.3', value: '3' },
        { name: 'documents:report.type.4', value: '4' },
        { name: 'documents:report.type.6', value: '6' },
        { name: 'documents:report.type.8', value: '8' },
    ]);

    const [filters, setFilters] = useState<Array<Filter>>(setDefaultFilters());
    const [orders, setOrders] = useState<Array<Order>>(setDefaultOrders());

    const clearFilter = (): void => {
        setFilters(setDefaultFilters());
        setOrders(setDefaultOrders());
        setShowClearFilterButton(false);
        dispatch({
            type: FilterReducerFieldName.HEADER_FILTER_MANAGE,
            clearAll: true,
        });
        dispatch({
            type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
        });
        setReload(true);
    };

    useEffect(() => {
        const filterManage = new FilterManager();
        if (headerFilterElementName) {
            if (headerFilterElementHasValue) {
                filterManage.manageElement(headerFilterElementName, headerFilterElementHasValue);
            } else {
                filterManage.manageElement(headerFilterElementName, false);
            }
            setShowClearFilterButton(filterManage.hasManageElement());
        }
    }, [headerFilterElementName, headerFilterElementHasValue, dispatch]);

    const paginationCallbackFunction = useCallback(
        (uri: string): void => {
            showMainLoader(true);
            const itemLoader = new ItemLoaders(dispatch);

            itemLoader.paginationCallWithCallback(
                uri,
                documentConstants.INVOICES_LIST,
                (items: DocumentCollectionInterface): void => {
                    setReports(items);
                    showMainLoader(false);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        },
        [showMainLoader, setReports, dispatch]
    );

    const updateOrderList = (orders: Array<Order>): void => setOrders(orders);
    const updateFilterList = (filters: Array<Filter>): void => {
        let foundFilterOverwrite = false;
        let foundAtLeastOneType = false;
        let parsedFilters: Array<Filter> = [];
        const prepareFilters: Array<Filter> = [];
        _.each(filters, (filter: Filter) => {
            if (filter.name === 'orType') {
                parsedFilters.push(filter);
                foundFilterOverwrite = true;
                foundAtLeastOneType = true;
            } else if (filter.name === 'orType[]') {
                prepareFilters.push(filter);
                foundAtLeastOneType = true;
            } else {
                parsedFilters.push(filter);
            }
        });
        if (!foundAtLeastOneType) {
            parsedFilters = [...parsedFilters, ...setDefaultFilters()];
        }
        if (!foundFilterOverwrite) {
            parsedFilters = [...parsedFilters, ...prepareFilters];
        }
        setFilters(parsedFilters);
    };

    const firstLoad = useCallback((): void => {
        showMainLoader(true);

        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/documents')),
            documentConstants.INVOICES_LIST,
            PreprocessFilters(filters),
            orders,
            (items: DocumentCollectionInterface): void => {
                setReports(items);
                showMainLoader(false);

                dispatch({
                    type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
                    showRender: true,
                });
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    }, [showMainLoader, filters, orders, itemLoader, dispatch, setReports]);

    useEffect(() => {
        if (reload) {
            setReload(false);
            firstLoad();
        }
    }, [reload, showMainLoader, firstLoad]);

    useEffect(() => {
        setReload(true);
    }, []);

    const saveCallback = useCallback(
        (params: any): void => {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.postWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(`/request_documents`)),
                documentConstants.REQUEST_DOCUMENT,
                params,
                (): void => {
                    setShowAdd(false);
                    setReload(true);
                },
                { propagateDataToCallback: false, dispatchAfterCallback: false, dispatchErrorAfterCallback: true }
            );
        },
        [dispatch, setReload]
    );

    const synchronizePivot = useCallback(
        (document: DocumentInterface, type = 'document_set'): void => {
            const itemLoader = new ItemLoaders(dispatch);

            itemLoader.postWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/custom_document_actions')),
                documentConstants.INVOICE_PUT,
                { action: 'pivot.synchronize', params: JSON.stringify({ id: document.id, type: 'document' }) },
                firstLoad,
                {
                    propagateDataToCallback: false,
                    dispatchAfterCallback: false,
                    dispatchErrorAfterCallback: false,
                }
            );
        },
        [dispatch, firstLoad]
    );

    const requestFileDownload = useCallback(
        (document: DocumentInterface, type = 'document_set'): void => {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(`/download_documents/${type}/${document.downloadHash}`)),
                documentConstants.FILE_DOWNLOAD,
                (data: any) => {
                    DownloadFile(data, document);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false, dispatchErrorAfterCallback: false }
            );
        },
        [dispatch]
    );

    return (
        <>
            <GridContainer>
                <GridItem xs={12} md={12} lg={12}>
                    <PaperedTable
                        beforeTableContent={
                            <GridContainer>
                                <GridItem xs={12} className={classes.beforeTableContent}>
                                    <CustomTextButtom
                                        callback={(): void => {
                                            props.showMainLoader(true);
                                            setReload(true);
                                        }}
                                        callbackParam={{}}
                                        color="primary"
                                        label="general:common.search"
                                        size="lg"
                                    />
                                    {showClearFilterButton && (
                                        <CustomTextButtom
                                            callback={clearFilter}
                                            callbackParam={{}}
                                            color="primary"
                                            label="general:common.clear"
                                            size="lg"
                                        />
                                    )}
                                    <FilterList />
                                </GridItem>
                            </GridContainer>
                        }
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell classes={tableStickyCommand} padding="none" align="left">
                                    <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true} showSort={false} />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="documents:report.report_type"
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        fieldName="orType"
                                        showSort={true}
                                        searchFieldType="select"
                                        selectComponent="headerCheckBoxSimpleList"
                                        elementList={reportType}
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} padding="none" align="left">
                                    <TableHeaderContentCell
                                        fieldName="rangeStart"
                                        textAlign="left"
                                        title="documents:report.period_start"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="asc"
                                        searchFieldType="date"
                                        dateFormat="RRRR-MM-DD"
                                        dateFromTo={false}
                                        filters={filters}
                                        sort={orders}
                                        breakLine={false}
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        nullable={false}
                                        autoComplete={'off'}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} padding="none" align="left">
                                    <TableHeaderContentCell
                                        fieldName="rangeEnd"
                                        textAlign="left"
                                        title="documents:report.period_end"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="asc"
                                        searchFieldType="date"
                                        dateFormat="RRRR-MM-DD"
                                        dateFromTo={false}
                                        filters={filters}
                                        sort={orders}
                                        breakLine={false}
                                        updateOrderList={updateOrderList}
                                        updateFilterList={updateFilterList}
                                        nullable={false}
                                        autoComplete={'off'}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {reports &&
                                reports['hydra:member'] &&
                                reports['hydra:member'].map((report: DocumentInterface) => {
                                    return (
                                        <ReportItem
                                            key={'report_item_' + report.id}
                                            report={report}
                                            downloadFileCallback={requestFileDownload}
                                            synchronizePivot={synchronizePivot}
                                        />
                                    );
                                })}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TableCell colSpan={4}>
                                    {reports && reports['hydra:member'].length > 0 && reports['hydra:view'] && (
                                        <Pager callback={paginationCallbackFunction} views={reports} />
                                    )}
                                </TableCell>
                            </TableRow>
                        </TableFooter>
                    </PaperedTable>
                </GridItem>
            </GridContainer>
            <FabAddButton
                callback={(): void => {
                    setShowAdd(true);
                }}
            />
            <ARReportAddDialog
                openView={showAdd}
                fullWidth={true}
                closeCallback={(): void => {
                    setShowAdd(false);
                }}
                saveCallback={saveCallback}
            />
        </>
    );
};

export default withStyles(oplAppStyle)(ARReportList);
